import type { ScoredGeography } from '@/types/insights';
import type { ReportFilters } from '@/types/report';
import { TEXAS_CITIES, TEXAS_COUNTIES } from '@/geo/texasCounties';

/**
 * Resolves a city geoId (e.g. "houston-tx") to its home county's FIPS geoId
 * (e.g. "48201"). Returns null if the city isn't found. See ReportFilters'
 * cityGeoId doc comment for why city selection maps to a county rather than
 * an independent dataset.
 */
export function resolveCityToCountyGeoId(cityGeoId: string): string | null {
  const city = TEXAS_CITIES.find((c) => c.geoId === cityGeoId);
  return city?.countyFips ?? null;
}

export function countyNameFor(countyGeoId: string): string | undefined {
  return TEXAS_COUNTIES.find((c) => c.geoId === countyGeoId)?.name;
}

/**
 * Applies the four non-linear filters (county, city, date range, price
 * range) to the full statewide geography set. Every filter is optional and
 * independent -- callers may set any subset, in any combination.
 *
 * - county/city filters narrow to a single county (city resolves to its
 *   parent county, since there is no independent city-level dataset -- see
 *   README's "Report Builder" section).
 * - date range filters against each record's publishedAt (the date its
 *   source snapshot claims as current-as-of). Today's data pipeline only
 *   carries one snapshot per source, so this mostly acts as an
 *   include/exclude-everything toggle rather than a true historical slice --
 *   it's wired up correctly now so it does more once multiple dated
 *   snapshots exist per geography.
 * - price range filters against resolved.medianSalePrice. Rows with no
 *   price on file are excluded whenever a price filter is active (can't
 *   evaluate a bound against a missing value).
 */
export function filterGeographiesForReport(
  geographies: ScoredGeography[],
  filters: ReportFilters,
): ScoredGeography[] {
  let targetCountyGeoId: string | null | undefined = filters.countyGeoId;
  if (!targetCountyGeoId && filters.cityGeoId) {
    targetCountyGeoId = resolveCityToCountyGeoId(filters.cityGeoId);
  }

  return geographies.filter(({ record }) => {
    if (targetCountyGeoId && record.geoId !== targetCountyGeoId) return false;

    if (filters.dateStart || filters.dateEnd) {
      const published = record.resolved.publishedAt;
      if (!published) return false;
      const publishedDate = published.slice(0, 10); // compare yyyy-mm-dd only
      if (filters.dateStart && publishedDate < filters.dateStart) return false;
      if (filters.dateEnd && publishedDate > filters.dateEnd) return false;
    }

    if (filters.priceMin != null || filters.priceMax != null) {
      const price = record.resolved.medianSalePrice;
      if (price == null) return false;
      if (filters.priceMin != null && price < filters.priceMin) return false;
      if (filters.priceMax != null && price > filters.priceMax) return false;
    }

    return true;
  });
}
